import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  listSurveysFromCloud,
  listDeploymentsFromCloud,
  listRecipientsFromCloud,
} from '../utils/cloudApi';
import { normalizeLifecycleStatus, LIFECYCLE_READY } from '../utils/lifecycleUtils';

/**
 * Survey/deployment data layer (refactor Step 5a). Owns the Console's core
 * server data — surveys, deployments, recipients — plus the offline snapshot,
 * the recipients cache, and the derived lifecycle views (WP3: surveys.status
 * is the single source of truth; panels render FROM the server field).
 * All logic moved verbatim from App.js.
 *
 * Params:
 *  - authLoading, user: from useAuth(). The first fetch waits for both —
 *    without user.org_id the backend rejects with 403 MISSING_ORG_CONTEXT.
 *  - isOnline, retryQueueRef: from useConnectivity(). Failed loads queue a
 *    retry while offline; refresh() falls back to the cached snapshot.
 *  - setLoading, onError: App-global busy flag and error banner.
 *
 * Returns data, derived views, and actions:
 *  - refresh(): reload surveys + deployments + per-deployment recipients.
 *  - loadRecipients(deploymentId): cached (5 min) recipient load. Selection
 *    is the caller's concern — App sets selectedDeploymentId first.
 *  - lastSnapshotRef: last-good data snapshot (shared with useSRA, which
 *    writes sessionsBySurveyId into it).
 */
export default function useSurveyData({ authLoading, user, isOnline, retryQueueRef, setLoading, onError }) {
  const [surveys, setSurveys] = useState([]);
  const [deployments, setDeployments] = useState([]);
  // Final n = y/x per closed deployment, captured from the /undeploy response so
  // the Dispatched panel's history rows are self-explanatory (WP3 §6).
  const [deploymentCounts, setDeploymentCounts] = useState({});
  const [recipients, setRecipients] = useState([]);
  const [recipientsByDeployment, setRecipientsByDeployment] = useState(new Map());
  const [search, setSearch] = useState('');
  const [corsBanner, setCorsBanner] = useState(null); // { message }
  // Phase 18/19: caching + offline snapshot
  const recipientsCacheRef = useRef({}); // { [deploymentId]: { ts, rows } }
  const lastSnapshotRef = useRef(null);

  // Helper: determine if a deployment is open/active
  const isOpenDeployment = useCallback((d) => d && (d.is_open === true || d.status === 'open' || d.status === 'active'), []);

  // Capture the shared n = y/x counts a lifecycle response carries, so the
  // Dispatched history and any UI reading final numbers stay consistent.
  const rememberDeploymentCounts = useCallback((deploymentId, counts) => {
    if (!deploymentId || !counts) return;
    setDeploymentCounts(prev => ({ ...prev, [deploymentId]: counts }));
  }, []);

  // Memorized recipients loader used across effects and handlers
  const loadRecipients = useCallback(async (deploymentId) => {
    setLoading(true); onError('');
    try {
      const now = Date.now();
      const cache = recipientsCacheRef.current[deploymentId];
      if (cache && (now - cache.ts) < 5 * 60 * 1000) {
        setRecipients(cache.rows || []);
      } else {
        const rows = await listRecipientsFromCloud(deploymentId);
        recipientsCacheRef.current[deploymentId] = { ts: now, rows: rows || [] };
        setRecipients(rows || []);
      }
    } catch (e) { onError(e.message); if (!isOnline) retryQueueRef.current.push(() => loadRecipients(deploymentId)); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  const refresh = useCallback(async () => {
    if (process.env.NODE_ENV !== 'production') { try { console.info('[API]', 'refresh start'); } catch (_) { } }
    setLoading(true); onError('');
    try {
      const s = await listSurveysFromCloud({ limit: 200, offset: 0, search, orgId: user?.org_id || '' });
      const d = await listDeploymentsFromCloud({ orgId: user?.org_id || '' });
      setSurveys((s.items || []).map(item => ({ ...item, survey_id: item.id || item.survey_id })));
      setDeployments(d || []);
      if (process.env.NODE_ENV !== 'production') { try { console.info('[API]', 'surveys/deployments loaded', { surveys: (s.items || []).length, deployments: (d || []).length }); } catch (_) { } }
      const map = new Map();
      await Promise.all((d || []).map(async (dep) => {
        try {
          const rows = await listRecipientsFromCloud(dep.deployment_id);
          map.set(dep.deployment_id, rows || []);
        } catch (_) { }
      }));
      setRecipientsByDeployment(map);
      if (process.env.NODE_ENV !== 'production') { try { console.info('[API]', 'recipients loaded', { deployments: (d || []).length }); } catch (_) { } }
      // Cache last successful snapshot for offline viewing
      try {
        const recipientsObj = Object.fromEntries(Array.from(map.entries()));
        const existing = lastSnapshotRef.current || {};
        lastSnapshotRef.current = {
          ts: Date.now(),
          surveys: s.items || [],
          deployments: d || [],
          recipientsByDeployment: recipientsObj,
          sessionsBySurveyId: existing.sessionsBySurveyId || {},
          exchangesBySessionId: existing.exchangesBySessionId || {}
        };
        sessionStorage.setItem('console_last_snapshot', JSON.stringify(lastSnapshotRef.current));
      } catch (_) { }
    } catch (e) {
      if (e && e.code === 'CORS_POLICY_VIOLATION') {
        setCorsBanner('CORS policy violation (403): kernel rejected origin');
      } else {
        onError(e.message);
        // Phase 19: queue a retry if offline
        if (!isOnline) {
          retryQueueRef.current.push(() => refresh());
          // Fallback to cached data if available
          try {
            const snap = lastSnapshotRef.current || JSON.parse(sessionStorage.getItem('console_last_snapshot') || 'null');
            if (snap) {
              setSurveys(snap.surveys || []);
              setDeployments(snap.deployments || []);
              const map = new Map(Object.entries(snap.recipientsByDeployment || {}));
              setRecipientsByDeployment(map);
            }
          } catch (_) { }
        }
      }
    } finally {
      setLoading(false);
      if (process.env.NODE_ENV !== 'production') { try { console.info('[API]', 'refresh complete'); } catch (_) { } }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, isOnline, user?.org_id]);

  // Wait for auth to complete AND org context to be available before the
  // first data fetch. Without user.org_id the backend rejects with 403
  // MISSING_ORG_CONTEXT, so there is no point calling refresh() early.
  useEffect(() => {
    if (!authLoading && user?.org_id) refresh();
  }, [authLoading, user?.org_id, refresh]);

  // ---- Server lifecycle state (WP3) ---------------------------------------
  // surveys.status is the single source of truth for the Ready/Deployed/Staged
  // panels and the Staging Area. Map each survey to its normalized lifecycle
  // state so the panels can render FROM the server field, not from localStorage.
  const surveyStatusById = useMemo(() => {
    const m = new Map();
    (surveys || []).forEach(s => m.set(s.survey_id, normalizeLifecycleStatus(s, deployments)));
    return m;
  }, [surveys, deployments]);

  // Open deployments whose survey is currently 'staged' server-side. This is the
  // authoritative list the Staging Area's linked-survey list is derived from.
  const stagedSurveys = useMemo(() => {
    return (deployments || [])
      .filter(d => (d.is_open === true) && surveyStatusById.get(d.survey_id) === 'staged')
      .map(d => {
        const s = (surveys || []).find(x => x.survey_id === d.survey_id);
        return {
          deploymentId: d.deployment_id,
          surveyId: d.survey_id,
          surveyName: d.survey_name || s?.name || d.survey_id,
          deploymentSlug: d.deploy_slug || d.deployment_slug || d.slug || d.deployment_id,
        };
      });
  }, [deployments, surveyStatusById, surveys]);

  // A stable signature so the reconcile effect fires only when the staged set
  // actually changes, not on every render.
  const stagedSignature = useMemo(
    () => stagedSurveys.map(s => s.deploymentId).sort().join('|'),
    [stagedSurveys]
  );

  const readyToDeploy = useMemo(() => {
    // WP3: the Ready panel renders FROM the server lifecycle state. Only surveys
    // whose surveys.status is 'ready' are editable/deployable here; deployed,
    // staged, and sent surveys live in the Deployed panel / Staging Area /
    // Dispatched history respectively (the survey is immutable once past Ready).
    return (surveys || [])
      .filter(s => surveyStatusById.get(s.survey_id) === LIFECYCLE_READY)
      .slice()
      .sort((a, b) => {
        const ta = new Date(a.updated_at || a.last_updated || 0).getTime();
        const tb = new Date(b.updated_at || b.last_updated || 0).getTime();
        return tb - ta;
      });
  }, [surveys, surveyStatusById]);

  const latestDeploymentForSurvey = useCallback((surveyId) => {
    const list = (deployments || []).filter(d => d.survey_id === surveyId);
    list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return list[0] || null;
  }, [deployments]);

  return {
    // core data
    surveys,
    deployments,
    deploymentCounts, rememberDeploymentCounts,
    recipients,
    recipientsByDeployment,
    search, setSearch,
    corsBanner,
    // derived lifecycle views (WP3)
    isOpenDeployment,
    surveyStatusById,
    stagedSurveys,
    stagedSignature,
    readyToDeploy,
    latestDeploymentForSurvey,
    // caches
    lastSnapshotRef,
    // actions
    refresh,
    loadRecipients,
  };
}
